import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  CardMedia,
  IconButton,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { RootState } from '../store/store';
import { buildEventsApiUrl } from '../config/api';
import { fetchCategories } from '../services/apiService';
import { getAuthUserId } from '../utils/authUser';

const conditions = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];

const EditItem: React.FC = () => {
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const { user, isAuthenticated, initialized } = useSelector((state: RootState) => state.auth);
  const [authChecked, setAuthChecked] = useState(false);
  const authUserId = getAuthUserId(user as any);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [condition, setCondition] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('active');
  const [isCashItem, setIsCashItem] = useState(false);
  const [cashValueUSD, setCashValueUSD] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingItem, setLoadingItem] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [pendingTradeEventName, setPendingTradeEventName] = useState('');
  const fetchGenerationRef = useRef(0);

  const isTradePending = status === 'trade-pending';

  // Wait for auth restoration before deciding whether to redirect.
  useEffect(() => {
    if (!initialized) return;
    setAuthChecked(true);
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [initialized, isAuthenticated, navigate]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data || []);
      } catch (err) {
        console.error('Failed to load categories:', err);
        setCategories([]);
      }
    };

    loadCategories();
  }, []);

  const fetchItemData = useCallback(async () => {
    const fetchGeneration = ++fetchGenerationRef.current;
    try {
      setLoadingItem(true);
      setError(null);
      setSuccess(false);
      setName('');
      setDescription('');
      setCategory('');
      setCondition('');
      setNotes('');
      setStatus('active');
      setIsCashItem(false);
      setCashValueUSD('');
      setImages([]);
      setTags([]);
      setPendingTradeEventName('');

      // Use direct endpoint with retries for eventual consistency
      let item = null;
      let retries = 0;
      const maxRetries = 5;

      while (!item && retries < maxRetries) {
        try {
          console.log('🧭 EditItem fetch start', { requestedItemId: itemId, retries });
          const response = await fetch(`${buildEventsApiUrl(`/api/items/${itemId}`)}`);
          const data = await response.json();
          if (data.success && data.item) {
            item = data.item;
            console.log('✅ EditItem fetched payload', {
              requestedItemId: itemId,
              returnedItemId: item.id,
              returnedItemName: item.name,
              returnedStatus: item.status,
              pendingTradeEventName: item.pendingTradeEventName || null,
            });
            break;
          }
        } catch (err) {
          retries++;
          if (retries < maxRetries) {
            console.log(`ℹ️ Retry ${retries}/${maxRetries} fetching item...`);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
      }

      if (item) {
        if (fetchGeneration !== fetchGenerationRef.current) return;
        setName(item.name || '');
        setDescription(item.description || '');
        const nextCategory = item.category || item.itemCategory || '';
        setCategory(nextCategory);
        setCondition(item.condition || '');
        setNotes(item.notes || '');
        setStatus(item.status || 'active');
        setIsCashItem(Boolean(item.isCashItem));
        setCashValueUSD(item.cashValueUSD ? String(item.cashValueUSD) : '');
        setPendingTradeEventName(item.pendingTradeEventName || '');
        const extractedUrls = (item.images || []).map((img: any) => {
          const url = typeof img === 'string' ? img : (img?.url || '');
          return url;
        });
        console.log('Extracted image URLs:', extractedUrls);
        setImages(extractedUrls);
        setTags(item.tags || []);
      } else {
        throw new Error('Item not found after retries');
      }
    } catch (err) {
      if (fetchGeneration !== fetchGenerationRef.current) return;
      console.error('❌ Error loading item:', err);
      setError('Failed to load item');
    } finally {
      if (fetchGeneration !== fetchGenerationRef.current) return;
      setLoadingItem(false);
    }
  }, [itemId]);


  // Fetch item only if authenticated
  useEffect(() => {
    if (authChecked && isAuthenticated && itemId) {
      fetchItemData();
    }
  }, [authChecked, isAuthenticated, itemId, fetchItemData]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (!files || !itemId) return;

    setUploadingImage(true);
    setError(null);

    for (let i = 0; i < Math.min(files.length, 6 - images.length); i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('image', file);
      formData.append('userId', authUserId || '');

      try {
        console.log(`📸 Uploading image: ${file.name}`);
        const response = await fetch(`${buildEventsApiUrl(`/api/items/${itemId}/upload-image`)}`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const result = await response.json();
        if (result.success && result.imageUrl) {
          console.log(`✅ Image uploaded: ${result.imageUrl}`);
          setImages(prev => [...prev, result.imageUrl]);
        } else {
          throw new Error(result.error || 'Upload failed');
        }
      } catch (err) {
        console.error('❌ Upload error:', err);
        setError(`Failed to upload image: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    setUploadingImage(false);
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag) && tags.length < 10) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isTradePending) {
      setError(`This item is trade pending${pendingTradeEventName ? ` for ${pendingTradeEventName}` : ''} and cannot be edited until the trade is completed or released.`);
      return;
    }
    if (!name || !description || (!isCashItem && !category) || (!isCashItem && !condition)) {
      setError('Name, description, category, and condition required');
      return;
    }
    if (isCashItem) {
      const normalizedCashValueUSD = Number(cashValueUSD);
      if (!Number.isFinite(normalizedCashValueUSD) || normalizedCashValueUSD <= 0) {
        setError('Cash amount must be greater than zero');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${buildEventsApiUrl(`/api/items/${itemId}`)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          description,
          category: isCashItem ? 'Cash Offer' : category,
          condition: isCashItem ? 'Cash' : condition,
          notes,
          status,
          images: isCashItem ? [] : images,
          tags,
          isCashItem,
          cashValueUSD: isCashItem ? Number(cashValueUSD) : 0,
          userId: authUserId
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 409) {
          throw new Error(data.error || 'Trade-pending items cannot be edited.');
        }
        throw new Error(data.error || 'Failed to update item');
      }

      console.log(`✅ Item updated: ${itemId}`);
      setSuccess(true);
      setTimeout(() => navigate('/dashboard?tab=my-items'), 1500);
    } catch (err) {
      console.error('❌ Submit error:', err);
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isTradePending) {
      setError(`This item is trade pending${pendingTradeEventName ? ` for ${pendingTradeEventName}` : ''} and cannot be deleted until the trade is completed or released.`);
      return;
    }
    if (!window.confirm('Delete this item permanently?')) return;
    try {
      setLoading(true);
      setError(null);
      console.log('🗑️ EditItem delete request', { itemId, authUserId });
      const response = await fetch(`${buildEventsApiUrl(`/api/items/${itemId}`)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: authUserId })
      });
      const data = await response.json().catch(() => ({}));
      console.log('🗑️ EditItem delete response', { status: response.status, ok: response.ok, data });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/dashboard?tab=my-items'), 1500);
      } else if (response.status === 409 && Array.isArray(data.blockingEvents) && data.blockingEvents.length > 0) {
        const eventNames = data.blockingEvents.map((event: any) => event.eventName || event.eventID || 'Unknown event').join(', ');
        setError(`Cannot delete this item because it is part of locked or cutoff-passed trade events: ${eventNames}`);
      } else {
        setError(data.error || 'Delete failed');
      }
    } catch (err) {
      console.error('🗑️ EditItem delete failed', err);
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while auth initializes
  if (!authChecked) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (loadingItem) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button onClick={() => navigate('/dashboard?tab=my-items')} startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
        Back to Dashboard
      </Button>
      <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
        Edit Item
      </Typography>

      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>Saved! Redirecting...</Alert>}
          {isTradePending && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This item is trade pending{pendingTradeEventName ? ` for ${pendingTradeEventName}` : ''}. Name, description, condition, notes, images, tags, and delete actions are locked until the trade is completed or released.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={isCashItem}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsCashItem(checked);
                        if (checked) {
                          setCategory('Cash Offer');
                          setCondition('Cash');
                          setImages([]);
                        } else {
                          if (category === 'Cash Offer') setCategory('');
                          if (condition === 'Cash') setCondition('Good');
                          setCashValueUSD('');
                        }
                      }}
                      disabled={loading || isTradePending}
                    />
                  )}
                  label="This is a cash offer item"
                />
              </Grid>

              {/* Basic Info */}
              <Grid item xs={12}>
                <TextField label="Item Name" fullWidth required value={name} onChange={(e) => setName(e.target.value)} disabled={loading || isTradePending} placeholder={isCashItem ? 'e.g., $20 Cash Offer' : undefined} />
              </Grid>

              {isCashItem && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Cash Amount ($)"
                    fullWidth
                    required
                    type="number"
                    value={cashValueUSD}
                    onChange={(e) => setCashValueUSD(e.target.value)}
                    inputProps={{ min: 0.01, step: '0.01' }}
                    disabled={loading || isTradePending}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField label="Description" fullWidth required multiline rows={4} value={description} onChange={(e) => setDescription(e.target.value)} disabled={loading || isTradePending} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select value={isCashItem ? 'Cash Offer' : category} label="Category" onChange={(e) => setCategory(e.target.value)} disabled={loading || isTradePending || isCashItem}>
                    {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    <MenuItem value="Cash Offer">Cash Offer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Condition</InputLabel>
                  <Select value={isCashItem ? 'Cash' : condition} label="Condition" onChange={(e) => setCondition(e.target.value)} disabled={loading || isTradePending || isCashItem}>
                    {conditions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    <MenuItem value="Cash">Cash</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField label="Additional Notes" fullWidth multiline rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={loading || isTradePending} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)} disabled={loading || isTradePending}>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Divider sx={{ width: '100%' }} />

              {/* Images */}
              {!isCashItem && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Images ({images.length}/6)</Typography>
                {!itemId && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    💾 Save the item first before you can upload images
                  </Alert>
                )}
                <Paper sx={{ p: 3, backgroundColor: '#f5f5f5', textAlign: 'center', cursor: 'pointer', border: '2px dashed #ccc', transition: 'all 0.3s' }}>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={loading || uploadingImage || isTradePending || images.length >= 6 || !itemId}
                    style={{ display: 'none' }}
                    id="image-input"
                  />
                  <label htmlFor="image-input" style={{ cursor: 'pointer', display: 'block' }}>
                    <PhotoCameraIcon sx={{ fontSize: 48, color: '#666', mb: 1 }} />
                    <Typography>Click to upload images</Typography>
                    {uploadingImage && <CircularProgress size={24} sx={{ mt: 1 }} />}
                  </label>
                </Paper>
              </Grid>
              )}

              {/* Image Gallery */}
              {!isCashItem && images.length > 0 && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {images.map((img, idx) => {
                      const imageUrl = String(img || '');
                      return (
                      <Grid item xs={6} sm={4} md={3} key={idx}>
                        <Card>
                          <CardMedia
                            component="img"
                            height="200"
                            image={imageUrl}
                            alt={`Item ${idx + 1}`}
                            sx={{ objectFit: 'cover' }}
                          />
                          <CardContent sx={{ p: 1 }}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeImage(idx)}
                              disabled={loading || uploadingImage}
                              sx={{ width: '100%' }}
                            >
                              <DeleteIcon /> Remove
                            </IconButton>
                          </CardContent>
                        </Card>
                      </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ width: '100%' }} />

              {/* Tags */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Tags ({tags.length}/10)</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="Add tag"
                    size="small"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    disabled={loading || isTradePending || tags.length >= 10}
                  />
                  <Button onClick={addTag} variant="outlined" disabled={loading || isTradePending || tags.length >= 10 || !tagInput.trim()}>
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => removeTag(tag)}
                      color="primary"
                      disabled={loading}
                    />
                  ))}
                </Box>
              </Grid>

              <Divider sx={{ width: '100%' }} />

              {/* Actions */}
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  disabled={loading || uploadingImage || isTradePending}
                >
                  Delete Item
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading || uploadingImage || isTradePending}
                  sx={{ px: 4 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EditItem;
















